"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import Link from "next/link"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { login, register, checkEmailExists } from "@/lib/api"
import { useAuth } from "@/contexts/AuthContext"
import { PasswordInput } from "@/components/ui/password-input"
import { authPageShellClassName } from "@/lib/mobile-bottom-nav"

const loginSchema = z.object({
  email: z.string().email("Email nije validan"),
  password: z.string().min(6, "Lozinka mora imati najmanje 6 karaktera"),
})

const signupSchema = z.object({
  ime: z.string().min(1, "Ime je obavezno"),
  prezime: z.string().min(1, "Prezime je obavezno"),
  email: z.string().email("Email nije validan"),
  password: z.string().min(6, "Lozinka mora imati najmanje 6 karaktera"),
  confirmPassword: z.string().min(6, "Potvrda lozinke mora imati najmanje 6 karaktera"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Lozinke se ne poklapaju",
  path: ["confirmPassword"],
})

type LoginFormValues = z.infer<typeof loginSchema>
type SignupFormValues = z.infer<typeof signupSchema>

export default function PrijavaPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const router = useRouter()
  const { login: setAuth, user, isLoading: authLoading } = useAuth()

  // Client-side redirect as fallback if middleware doesn't catch it
  useEffect(() => {
    if (!authLoading && user) {
      router.replace("/profil")
    }
  }, [user, authLoading, router])

  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      ime: "",
      prezime: "",
      email: "",
      password: "",
      confirmPassword: "",
  },
  })

  // Clear error when user changes any field
  const clearError = () => {
    if (error) {
      setError(null)
    }
  }

  // Check if email exists on blur
  const handleEmailBlur = async (email: string) => {
    if (!email || !email.includes('@')) {
      return // Don't check if email is empty or invalid
    }

    setCheckingEmail(true)
    try {
      const exists = await checkEmailExists(email)
      if (exists) {
        setError('Email adresa je već registrovana. Molimo koristite drugu email adresu ili se prijavite.')
        signupForm.setError('email', {
          type: 'manual',
          message: 'Email adresa je već registrovana.'
        })
      } else {
        // Clear the error if email doesn't exist
        // Remove the error by setting it to empty
        if (signupForm.formState.errors.email) {
          signupForm.setError('email', { type: 'manual', message: '' })
        }
      }
    } catch (error) {
      console.error('Error checking email:', error)
    } finally {
      setCheckingEmail(false)
    }
  }


  const onLoginSubmit = async (data: LoginFormValues) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await login(data.email, data.password)
      setAuth(response.jwt, response.user)
      router.push("/profil")
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Greška pri prijavljivanju"
      setError(errorMessage)
      console.error("Login error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const onSignupSubmit = async (data: SignupFormValues) => {
    setIsLoading(true)
    setError(null)
    setShowConfirmationMessage(false)
    try {
      const response = await register(data.ime, data.prezime, data.email, data.password)
      
      // If user has JWT, they're confirmed - log them in
      if (response.jwt && response.user) {
        setAuth(response.jwt, response.user)
        router.push("/profil")
      } else {
        // User needs email confirmation - show message
        setRegisteredEmail(data.email)
        setShowConfirmationMessage(true)
        signupForm.reset() // Clear the form
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Greška pri registraciji"
      setError(errorMessage)
      console.error("Signup error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={authPageShellClassName}>
      <div className="w-full max-w-md">
        <Tabs defaultValue="login" className="w-full">
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold text-center">
                Dobrodošli
              </CardTitle>
              <CardDescription className="text-center">
                Prijavite se na vaš nalog ili kreirajte novi
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                  {error}
                </div>
              )}
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger
                  value="login"
                  onClick={() => setError(null)}
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-input/30"
                >
                  Prijava
                </TabsTrigger>
                <TabsTrigger
                  value="signup"
                  onClick={() => setError(null)}
                  className="data-[state=active]:bg-white dark:data-[state=active]:bg-input/30"
                >
                  Registracija
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <Form {...loginForm}>
                  <form
                    onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                    className="space-y-4"
                  >
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="vas@email.com"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                clearError();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Lozinka</FormLabel>
                          <FormControl>
                            <PasswordInput
                              placeholder="••••••••"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                clearError();
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                    >
                      {isLoading ? "Prijavljivanje..." : "Prijavi se"}
                    </Button>
                    <div className="text-center mt-2">
                      <Link
                        href="/zaboravljena-lozinka"
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        Zaboravili ste lozinku?
                      </Link>
                    </div>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="signup">
                {showConfirmationMessage ? (
                  <div className="space-y-4">
                    <div className="p-4 text-sm bg-[#D1FAE5] dark:bg-[#D1FAE5]/30 border border-[#D1FAE5] dark:border-[#D1FAE5]/40 rounded-md">
                      <h3 className="font-semibold text-[#065F46] dark:text-[#D1FAE5] mb-2">
                        Uspešno ste se registrovali!
                      </h3>
                      <p className="text-[#065F46] dark:text-[#D1FAE5] mb-2">
                        Poslali smo vam email na adresu{" "}
                        <strong>{registeredEmail}</strong> sa linkom za potvrdu
                        vašeg naloga.
                      </p>
                      <p className="text-[#065F46] dark:text-[#D1FAE5]">
                        Molimo proverite vašu email poštu i kliknite na link da
                        biste potvrdili vašu email adresu. Nakon potvrde, možete
                        se prijaviti.
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setShowConfirmationMessage(false);
                        signupForm.reset();
                      }}
                      variant="outline"
                      className="w-full"
                    >
                      Zatvori
                    </Button>
                  </div>
                ) : (
                  <Form {...signupForm}>
                    <form
                      onSubmit={signupForm.handleSubmit(onSignupSubmit)}
                      className="space-y-4"
                    >
                      <FormField
                        control={signupForm.control}
                        name="ime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Ime</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                placeholder="Vaše ime"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="prezime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Prezime</FormLabel>
                            <FormControl>
                              <Input
                                type="text"
                                placeholder="Vaše prezime"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder="vas@email.com"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                                onBlur={async (e) => {
                                  field.onBlur();
                                  await handleEmailBlur(e.target.value);
                                }}
                                disabled={checkingEmail}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Lozinka</FormLabel>
                            <FormControl>
                              <PasswordInput
                                placeholder="••••••••"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={signupForm.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Potvrdi lozinku</FormLabel>
                            <FormControl>
                              <PasswordInput
                                placeholder="••••••••"
                                {...field}
                                onChange={(e) => {
                                  field.onChange(e);
                                  clearError();
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={isLoading}
                      >
                        {isLoading ? "Kreiranje naloga..." : "Kreiraj nalog"}
                      </Button>
                    </form>
                  </Form>
                )}
              </TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </div>
    </div>
  );
}

